
import React, { useState, useEffect, useMemo } from 'react';
import { CityStats, Milestone, Grid, BuildingType, ResearchState, SimulationBreakdown } from '../../types';
import { BUILDINGS, PRODUCTS, TECH_TREE } from '../../constants';
import { playUiSfx } from '../../utils/uiAudio';
import { generateAuditReport } from '../../utils/exportUtils';
import TabNavigation from './TabNavigation';
import DashboardTabHeader from './DashboardTabHeader';
import { getDashboardTabMeta } from './dashboardTabMeta';
import { ModalShell, surfaces, buttons, cn } from './kit';
import ChecklistTab from './tabs/ChecklistTab';
import ComplianceTab from './tabs/ComplianceTab';
import SecurityTab from './tabs/SecurityTab';
import ProductsTab from './tabs/ProductsTab';
import FinanceTab from './tabs/FinanceTab';
import OrgManagementTab from './tabs/OrgManagementTab';
import ResearchTab from './tabs/ResearchTab';

export type DashboardTab = 'checklist' | 'compliance' | 'security' | 'research' | 'products' | 'finance' | 'orgmanagement';

interface DashboardPanelProps {
    stats: CityStats;
    milestones: Milestone[];
    research: ResearchState;
    onTakeLoan: () => void;
    onRepayLoan: () => void;
    onPitchInvestors: () => void;
    onLaunchProduct: (id: string) => void;
    onStartResearch: (id: string) => void;
    onClose: () => void;
    onExitToMenu: () => void;
    grid: Grid;
    hasJumpGate: boolean;
    initialTab?: DashboardTab;
    simBreakdown?: SimulationBreakdown;
    orgManagementOpen: string | null;
    onSetOrgManagementOpen: (type: string | null) => void;
}

const DashboardPanel: React.FC<DashboardPanelProps> = ({ stats, milestones, research, onTakeLoan, onRepayLoan, onPitchInvestors, onLaunchProduct, onStartResearch, onClose, onExitToMenu, grid, hasJumpGate, initialTab = 'checklist', simBreakdown, orgManagementOpen, onSetOrgManagementOpen }) => {
    const [tab, setTab] = useState<DashboardTab>(initialTab);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    // Handle mobile responsiveness
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // Memoize grid counts to prevent recalculation on every render
    const counts = useMemo(() => {
        const counts: Record<string, number> = {};
        grid.flat().forEach(tile => {
            if (tile.buildingType !== BuildingType.None && tile.isConnected) {
                counts[tile.buildingType] = (counts[tile.buildingType] || 0) + 1;
            }
        });
        return counts;
    }, [grid]);

    // Keyboard Shortcuts for Tabs
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === '1') { playUiSfx('click'); setTab('checklist'); }
            if (e.key === '2') { playUiSfx('click'); setTab('compliance'); }
            if (e.key === '3') { playUiSfx('click'); setTab('security'); }
            if (e.key === '4') { playUiSfx('click'); setTab('research'); }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Master R&D Progress
    const renderMasterProgress = () => {
        const totalProjects = TECH_TREE.length;
        const completedProjects = research.completedTechs.length;
        const activeProject = research.activeResearchId;
        const completionPercentage = (completedProjects / totalProjects) * 100;

        return (
            <div className="mb-6 p-4 bg-slate-900/50 border border-slate-700 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-bold text-white">🔬 Master R&D Progress</h3>
                    <span className="text-sm text-slate-300">{completedProjects}/{totalProjects} Projects</span>
                </div>

                {/* Overall Progress Bar */}
                <div className="w-full bg-slate-800 rounded-full h-3 mb-3 overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-emerald-600 to-emerald-500 transition-all duration-500"
                        style={{ width: `${completionPercentage}%` }}
                    />
                </div>

                {/* Active Research Status */}
                {activeProject && (
                    <div className="mt-4 p-3 bg-cyan-900/20 border border-cyan-700/50 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-bold text-cyan-400">🔄 Currently Researching:</span>
                            <span className="text-xs text-slate-300">Progress</span>
                        </div>
                        <div className="text-sm text-white mb-2">
                            {TECH_TREE.find(t => t.id === activeProject)?.name}
                        </div>
                        {/* Enhanced Progress Bar for Active Research */}
                        <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-cyan-600 to-cyan-500 transition-all duration-300 relative"
                                style={{ width: `${research.researchProgress * 100}%` }}
                            >
                                <div className="absolute right-0 top-0 h-full w-1 bg-white animate-pulse"></div>
                            </div>
                        </div>
                        <div className="text-xs text-slate-400 mt-1 text-right">
                            {Math.floor(research.researchProgress * 100)}% Complete
                        </div>
                    </div>
                )}

                {/* Research Milestones */}
                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                    <div className="text-center">
                        <div className="text-slate-400">Tier 1</div>
                        <div className="text-white font-bold">
                            {research.completedTechs.filter(t => t.includes('tech_basic_sec')).length}/1
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-slate-400">Tier 2</div>
                        <div className="text-white font-bold">
                            {research.completedTechs.filter(t => t.includes('tech_compliance') || t.includes('tech_ops') || t.includes('tech_adv_cyber')).length}/3
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-slate-400">Tier 3</div>
                        <div className="text-white font-bold">
                            {research.completedTechs.filter(t => t.includes('tech_zerotrust') || t.includes('tech_admin') || t.includes('tech_soc2') || t.includes('tech_cloud_security') || t.includes('tech_threat_intel') || t.includes('tech_gdpr') || t.includes('tech_advanced_audit') || t.includes('tech_regulatory_affairs')).length}/7
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Filter Logic - Memoized for performance
    const renderResearchList = useMemo(() => (filterTag?: string) => {
        const filteredTech = filterTag 
            ? TECH_TREE.filter(t => t.id.includes(filterTag) || t.description.toLowerCase().includes(filterTag))
            : TECH_TREE;

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredTech.length === 0 && <div className="text-slate-500 italic">No available projects in this sector.</div>}
                {filteredTech.map(tech => {
                    const isCompleted = research.completedTechs.includes(tech.id);
                    const isActive = research.activeResearchId === tech.id;
                    const isLocked = !tech.prerequisites.every(pre => research.completedTechs.includes(pre));
                    
                    return (
                        <div key={tech.id} className={`p-5 rounded border relative overflow-hidden ${isCompleted ? 'bg-emerald-950/20 border-emerald-900' : isActive ? 'bg-cyan-900/10 border-cyan-500' : isLocked ? 'bg-black opacity-50 border-white/5' : 'bg-white/5 border-white/10'}`}>
                            {isActive && (
                                <div className="absolute bottom-0 left-0 h-2 bg-cyan-500 transition-all duration-300" style={{ width: `${research.researchProgress * 100}%` }}>
                                    <div className="h-full bg-gradient-to-t from-cyan-600 to-cyan-400 animate-pulse"></div>
                                </div>
                            )}
                            
                            <div className="flex justify-between items-start mb-2">
                                <h3 className={`font-bold uppercase tracking-wide ${isCompleted ? 'text-emerald-400' : 'text-white'}`}>{tech.name}</h3>
                                {isCompleted && <span className="text-[9px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded border border-emerald-500/30">COMPLETE</span>}
                                {isLocked && <span className="text-xl">🔒</span>}
                            </div>
                            
                            <p className="text-xs text-slate-400 mb-4 h-8">{tech.description}</p>
                            
                            <div className="flex gap-4 text-xs font-mono mb-4 text-slate-500">
                                <span>⏱ {tech.duration * 2}s</span>
                                <span className={`${stats.budget >= tech.cost ? 'text-slate-300' : 'text-red-400'}`}>${tech.cost}</span>
                            </div>
                            
                            {!isCompleted && !isLocked && (
                                <button
                                    onClick={() => { playUiSfx(isActive ? 'click' : 'money'); onStartResearch(tech.id); }}
                                    disabled={isActive || stats.budget < tech.cost}
                                    className={`w-full py-2 rounded text-xs font-bold uppercase tracking-widest ${isActive ? 'bg-cyan-600/50 text-white cursor-default' : stats.budget >= tech.cost ? 'bg-cyan-600 hover:bg-cyan-500 text-white' : 'bg-slate-800 text-slate-500 cursor-not-allowed'}`}
                                >
                                    {isActive ? `Researching... ${Math.floor(research.researchProgress * 100)}%` : `Start Project ($${tech.cost})`}
                                </button>
                            )}
                            {isLocked && (
                                    <div className="text-[9px] text-red-500 uppercase font-bold">Requires: {tech.prerequisites.map(id => TECH_TREE.find(t=>t.id===id)?.name).join(', ')}</div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }, [research, stats.budget, onStartResearch]);

    return (
        <ModalShell
            testId="dashboard-panel"
            zLevel="modal"
            variant="center"
            className={cn(surfaces.commandCenter, 'h-[90vh] md:h-[80vh] flex flex-col overflow-hidden')}
        >
                
                {/* Mobile Tab Navigation - Only shown on mobile */}
                {isMobile && (
                    <TabNavigation 
                        activeTab={tab} 
                        onTabChange={(newTab) => { playUiSfx('click'); setTab(newTab); }}
                        isMobile={isMobile}
                        onClose={() => { playUiSfx('click'); onClose(); }}
                        onExitToMenu={() => { playUiSfx('click'); onExitToMenu(); }}
                    />
                )}

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                    {/* Desktop Sidebar - Only shown on desktop */}
                    {!isMobile && (
                        <TabNavigation 
                            activeTab={tab} 
                            onTabChange={(newTab) => { playUiSfx('click'); setTab(newTab); }}
                            isMobile={isMobile}
                            onClose={() => { playUiSfx('click'); onClose(); }}
                            onExitToMenu={() => { playUiSfx('click'); onExitToMenu(); }}
                        />
                    )}
                    
                    {/* Tab Content */}
                    <div className="flex-1 p-4 md:p-8 overflow-y-auto bg-[#0a0a0a] bg-gradient-to-br from-[#0a0a0a] to-[#111]">
                        <DashboardTabHeader meta={getDashboardTabMeta(tab)} />
                        {tab === 'checklist' && (
                            <ChecklistTab 
                                stats={stats}
                                milestones={milestones}
                                grid={grid}
                                onExportAudit={() => generateAuditReport(stats, grid, research, milestones)}
                            />
                        )}
                        {tab === 'compliance' && <ComplianceTab stats={stats} grid={grid} simBreakdown={simBreakdown} />}
                        {tab === 'security' && <SecurityTab stats={stats} grid={grid} simBreakdown={simBreakdown} />}
                        {tab === 'research' && (
                            <ResearchTab 
                                stats={stats}
                                research={research}
                                onStartResearch={onStartResearch}
                            />
                        )}
                        {tab === 'products' && (
                            <ProductsTab 
                                stats={stats}
                                grid={grid}
                                onLaunchProduct={onLaunchProduct}
                            />
                        )}
                        {tab === 'finance' && (
                            <FinanceTab 
                                stats={stats}
                                simBreakdown={simBreakdown}
                                hasJumpGate={hasJumpGate}
                                onTakeLoan={onTakeLoan}
                                onRepayLoan={onRepayLoan}
                                onPitchInvestors={onPitchInvestors}
                            />
                        )}
                        {tab === 'orgmanagement' && (
                            <OrgManagementTab 
                                stats={stats}
                                grid={grid}
                                onSetOrgManagementOpen={onSetOrgManagementOpen}
                            />
                        )}
                    </div>
                </div>

                {/* Mobile Action Buttons */}
                {isMobile && (
                    <div className="bg-black border-t border-white/10 p-4 flex gap-2">
                        <button 
                            onClick={() => { playUiSfx('click'); onClose(); }} 
                            className={cn(buttons.ghost, 'flex-1 py-3')}
                        >
                            Resume
                        </button>
                        <button 
                            onClick={() => { 
                                if(window.confirm("Abort current simulation and return to main menu?")) {
                                    playUiSfx('click'); 
                                    onExitToMenu(); 
                                }
                            }} 
                            className={cn(buttons.danger, 'flex-1 py-3')}
                        >
                            Quit
                        </button>
                    </div>
                )}
        </ModalShell>
    );
};

export default DashboardPanel;
